// hooks/usePettyCashTransactions.js
import {
    usePaginatedResource,
    useResourceItem,
    useCreateResource,
    useEditResource,
    useDeleteResource,
} from "./useCrudResource";
import {
    getPettyCashTransactions,
    getPettyCashTransaction,
    createPettyCashTransaction,
    editPettyCashTransaction,
    deletePettyCashTransaction,
    getAllPettyCashTransactions,
    getTrashedPettyCashTransactions,
    restorePettyCashTransaction,
} from "api/pettyCashTransactionsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const PETTY_CASH_TRANSACTION_QUERY_KEY = "petty-cash-transactions";

// -------------------- FETCH PAGINATED --------------------
export const usePettyCashTransactions = (params = {}, enabled = true) => {
  return usePaginatedResource(
    PETTY_CASH_TRANSACTION_QUERY_KEY,
    getPettyCashTransactions,
    params,
    enabled
  );
};

// -------------------- FETCH SINGLE ITEM --------------------
export const usePettyCashTransaction = (id, enabled = true) => {
  return useResourceItem(PETTY_CASH_TRANSACTION_QUERY_KEY, getPettyCashTransaction, id);
};

// -------------------- CREATE --------------------
export const useCreatePettyCashTransaction = () => {
  return useCreateResource(createPettyCashTransaction, PETTY_CASH_TRANSACTION_QUERY_KEY);
};

// -------------------- EDIT --------------------
export const useEditPettyCashTransaction = () => {
  return useEditResource(editPettyCashTransaction, PETTY_CASH_TRANSACTION_QUERY_KEY);
};

// -------------------- DELETE --------------------
export const useDeletePettyCashTransaction = () => {
  return useDeleteResource(deletePettyCashTransaction, PETTY_CASH_TRANSACTION_QUERY_KEY);
};

// -------------------- ADDITIONAL --------------------

// Get all transactions without pagination
export const useAllPettyCashTransactions = (params = {}) => {
  return usePaginatedResource(
    PETTY_CASH_TRANSACTION_QUERY_KEY,
    getAllPettyCashTransactions,
    { ...params, all: true },
    true
  );
};

// Get trashed transactions
export const useTrashedPettyCashTransactions = (params = {}) => {
  return usePaginatedResource(
    PETTY_CASH_TRANSACTION_QUERY_KEY,
    getTrashedPettyCashTransactions,
    params,
    true
  );
};

// Restore a trashed transaction
export const useRestorePettyCashTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: restorePettyCashTransaction,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data;
      if (!restoredItem) return;

      // Update paginated cache
      queryClient.setQueriesData([PETTY_CASH_TRANSACTION_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old;
        const updated = structuredClone(old);
        updated.data.data = [restoredItem, ...updated.data.data];
        updated.data.total = (updated.data.total || 0) + 1;
        return updated;
      });

      // Update individual item cache
      queryClient.setQueryData([PETTY_CASH_TRANSACTION_QUERY_KEY, id], restoredItem);
    },
  });
};